import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../app/rootReducer";
import { getValueFromUrlParameter } from "../../../util/urlUtils";
import { AsinWithCatalogIssues } from "../../../from-server/asin-central/catalog-issues";
import {
  useCatalogIssueDetails,
  useCatalogIssuesSearch,
} from "./useCatalogIssues";
import { CatalogIssuesPanel } from "./CatalogIssuesPanel";

/**
 * URL parameter honored only by the mock BFF (STAGE=local): set
 * `?mockState=partial|unavailable|limitExceeded` in the browser to
 * exercise each degraded dependencyStatus.
 */
export const MOCK_STATE_URL_KEY = "mockState";

/**
 * Container for the Catalog Issues tab. Reads the submitted global filter
 * from the shared `generateQuery` slice, but keeps all fetch / selection
 * state LOCAL to this tab (per the frontend design doc) so that a slow or
 * failing catalog dependency can never block the Validation Dashboard
 * table or tab navigation.
 */
export const CatalogIssuesTabContent: React.FC = () => {
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );
  const mockState = getValueFromUrlParameter(MOCK_STATE_URL_KEY) || undefined;

  const search = useCatalogIssuesSearch(queryString, mockState);
  const { details, setSelection, reset } = useCatalogIssueDetails();
  const [selectedItems, setSelectedItems] = useState<AsinWithCatalogIssues[]>(
    []
  );

  // Clear the selection and its appended details whenever the global filter
  // changes, so stale ASINs never linger over a reloaded (or emptied) result set.
  useEffect(() => {
    setSelectedItems([]);
    reset();
  }, [queryString, reset]);

  const handleSelectionChange = useCallback(
    (items: AsinWithCatalogIssues[]) => {
      setSelectedItems(items);
      setSelection(items);
    },
    [setSelection]
  );

  return (
    <div data-testid="catalog-issues-tab-content">
      <CatalogIssuesPanel
        search={search}
        details={details}
        selectedItems={selectedItems}
        onSelectionChange={handleSelectionChange}
      />
    </div>
  );
};

export default CatalogIssuesTabContent;
